import { create } from 'zustand';
import { conditionalDevtools } from '@/lib/storeMiddleware';
import type { AccountConfig } from '@/types/email';
import { fetchAccountConfig } from '@/api/accounts';
import { getErrorMessage } from '@/lib/error';
import { useUIStore } from './uiStore';

interface AccountModalsState {
  editingAccountId: string | null;
  editingAccountConfig: AccountConfig | null;
  editingAccountLoading: boolean;
  fullRefreshPending: boolean;
  fastRecoveryPending: boolean;
  loadError: string | null;
  setEditingAccountId: (id: string | null) => void;
  setEditingAccountConfig: (config: AccountConfig | null) => void;
  setEditingAccountLoading: (loading: boolean) => void;
  setFullRefreshPending: (pending: boolean) => void;
  setFastRecoveryPending: (pending: boolean) => void;
  loadAccountConfig: (accountId: string) => Promise<void>;
  resetEditingState: () => void;
}

export const useAccountModalsStore = create<AccountModalsState>()(
  conditionalDevtools(
    (set) => ({
      editingAccountId: null,
      editingAccountConfig: null,
      editingAccountLoading: false,
      fullRefreshPending: false,
      fastRecoveryPending: false,
      loadError: null,

      setEditingAccountId: (id) => set({ editingAccountId: id }),
      setEditingAccountConfig: (config) => set({ editingAccountConfig: config }),
      setEditingAccountLoading: (loading) => set({ editingAccountLoading: loading }),
      setFullRefreshPending: (pending) => set({ fullRefreshPending: pending }),
      setFastRecoveryPending: (pending) => set({ fastRecoveryPending: pending }),

      loadAccountConfig: async (accountId) => {
        if (!accountId) {
          set({
            editingAccountConfig: null,
            editingAccountLoading: false,
            loadError: null,
          });
          return;
        }

        set({ editingAccountLoading: true, loadError: null, editingAccountConfig: null });
        try {
          const config = await fetchAccountConfig(accountId);
          set({ editingAccountConfig: config, editingAccountLoading: false });
        } catch (error) {
          set({
            editingAccountLoading: false,
            loadError: getErrorMessage(error) ?? 'Failed to load account settings',
          });
          useUIStore.getState().setGlobalError(getErrorMessage(error) ?? 'Failed to load account settings');
          throw error;
        }
      },

      resetEditingState: () =>
        set({
          editingAccountId: null,
          editingAccountConfig: null,
          editingAccountLoading: false,
          fullRefreshPending: false,
          fastRecoveryPending: false,
          loadError: null,
        }),
    }),
    { name: 'AccountModalsStore' }
  )
);
